import { useCallback, useEffect, useState } from "react";
import { Grid, IconButton, Chip, lighten, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useIntl } from "react-intl";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import useEditedStyles from "../../../../../hooks/useEditedStyles";
import { Partition, RfidCardValue, SmartalarmUser } from "../types";

interface RfidCardProps {
  index: number;
  value: RfidCardValue;
  saved: RfidCardValue | null;
  users: SmartalarmUser[];
  partitions: Partition[];
  salt: string;
  onChange: (index: number, value: RfidCardValue) => void;
  foundCard?: { index: number; number: string } | null;
  onClearFound?: () => void;
  disabled?: boolean;
}

const RfidCard = ({ index, value, saved, users, partitions, onChange, foundCard, onClearFound, disabled }: RfidCardProps) => {
  const [user, setUser] = useState(value.user);
  const [partition, setPartition] = useState(value.partition || "DEF");

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();

  const onDelete = useCallback(() => {
    value.deleted = true;
    onChange(index, value);
  }, [index, value, onChange]);

  const onRestore = useCallback(() => {
    delete value.deleted;
    onChange(index, value);
  }, [index, value, onChange]);

  const handleUserChange = useCallback(
    (usr: string) => {
      setUser(usr);
      value.user = usr;
      onChange(index, value);
    },
    [onChange, value, index]
  );

  const handlePartitionChange = useCallback(
    (part: string) => {
      setPartition(part);
      if (part !== "DEF") {
        value.partition = part;
      } else {
        delete value.partition;
      }
      onChange(index, value);
    },
    [onChange, value, index]
  );

  useEffect(() => {
    setUser(value.user);
  }, [value]);

  return (
    <Grid container spacing={1} flexDirection="row" alignItems="center">
      <Grid item>
        <FormControl
          variant="filled"
          size="small"
          sx={{ margin: 0, minWidth: 150, maxWidth: 150 }}
          className={saved?.user !== user || users.find((u) => u.uuid === user)?.deleted ? editedStyle.input : ""}
          disabled={disabled || users.length === 0 || value?.deleted}
          fullWidth
        >
          <InputLabel id="select-label">{formatMessage({ defaultMessage: "User" })}</InputLabel>
          <Select labelId="select-label" value={user} onChange={(e) => handleUserChange(e.target.value)}>
            {users
              .filter((u) => u.uuid === user || !u.deleted)
              .map((option) => (
                <MenuItem key={`user-${option.uuid}`} value={option.uuid}>
                  {[option.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |` : "", option.code, option.name]
                    .filter((i) => !!i)
                    .join(" ")}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item>
        <FormControl
          variant="filled"
          size="small"
          sx={{ margin: 0, minWidth: 200, maxWidth: 200 }}
          className={
            !(!saved?.partition && partition === "DEF") && (saved?.partition !== partition || partitions.find((p) => p.uuid === partition)?.deleted)
              ? editedStyle.input
              : ""
          }
          disabled={disabled || partitions.length === 0 || value?.deleted}
          fullWidth
        >
          <InputLabel id="select-label">{formatMessage({ defaultMessage: "Partition" })}</InputLabel>
          <Select labelId="select-label" value={partition} onChange={(e) => handlePartitionChange(e.target.value)}>
            <MenuItem key={`partition-none`} value={"DEF"} sx={{ fontStyle: "italic" }}>
              {formatMessage({ defaultMessage: "Default of the reader" })}
            </MenuItem>
            <MenuItem key="part-all" value="ALL" sx={{ "ul > &": { color: "secondary.main" } }}>
              {formatMessage({ defaultMessage: "All partitions" })}
            </MenuItem>
            {partitions
              .filter((p) => p.uuid === partition || !p.deleted)
              .map((option) => (
                <MenuItem key={`partition-${option.uuid}`} value={option.uuid}>
                  {[option.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |` : "", option.code, option.name]
                    .filter((i) => !!i)
                    .join(" ")}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      {!value.deleted && !disabled && (
        <Grid item>
          <IconButton onClick={onDelete}>
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Grid>
      )}
      {value.deleted && !disabled && (
        <>
          <Grid item>
            <Chip label={formatMessage({ defaultMessage: "deleted" })} size="small" className={editedStyle.chip} />
          </Grid>
          <Grid item>
            <Button size="small" onClick={onRestore} color="inherit">
              {formatMessage({ defaultMessage: "restore" })}
            </Button>
          </Grid>
        </>
      )}
      {foundCard?.index === index && (
        <Grid item>
          <Chip
            icon={<SearchIcon />}
            label={foundCard.number}
            color="success"
            variant="outlined"
            sx={{ backgroundColor: (theme) => lighten(theme.palette.success.main, 0.8) }}
            onDelete={onClearFound}
            deleteIcon={<ClearIcon />}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default RfidCard;
